import React, { useMemo } from 'react';

import { Flex } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { HeaderMarginContainer } from 'Deer/components/HeaderComponents/HeaderMarginContainer';
import { useQuery } from 'components/hooks/useQuery';
import { addYears, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import { useSelectedView } from 'reducers/viewsSlice';
import { oneOnOneLandingPageReactQueryKeys } from 'screens/oneOnOneRabbit/landingPage/constants/oneOnOneReactQueryKeys';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { past3Months } from 'constants/ProjectConstants';
import { isMobileView } from 'constants/isMobileView';
import { sendChangedTimelineEvent } from 'constants/mixpanelHelper';

import { DateRangeDropdown } from 'uiToolkitV1/DateRange';
import { TextHeader2 } from 'uiToolkitV1/Typography';

import { setDateRangeOneOnOneInsightsFilters } from '../slice/OneOnOneInsightsFiltersSlice';

const OneOnOneHeading = () => {
  const selectedCohort = useSelectedView();
  const queryParams = useQuery();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const updateDateRange = (dateRange) => {
    // Sending changed timeline event
    sendChangedTimelineEvent({
      eventProperties: {
        cohort: selectedCohort?.name,
        cohortId: selectedCohort?.id,
        source: '1:1s',
        type: 'insights',
        value: dateRange?.label ?? '',
      },
    });

    // Dispatching date range update
    dispatch(setDateRangeOneOnOneInsightsFilters({ dateRange }));
    queryClient.invalidateQueries([oneOnOneLandingPageReactQueryKeys.oneOnOneLandingPageDetails]);
    queryClient.invalidateQueries([oneOnOneLandingPageReactQueryKeys.oneOnOneActiveArchiveCount]);
  };

  // Generate updated titles based on the selected cohort
  const updatedTitle = useMemo(() => {
    let title = '';
    if (selectedCohort) {
      const { id, name } = selectedCohort;
      const isNewNavEnabled = checkNewNavEnabled();

      switch (id) {
        case -1:
          title = name === 'Settings' ? '1:1 Settings' : `My company's 1:1s for`;
          break;
        case -2:
          title = isNewNavEnabled ? "My direct team's 1:1s for" : "My Team's 1:1s for";
          break;
        case -3:
          title = isNewNavEnabled ? "My skip team's 1:1s for" : "My Organization's Tree 1:1s for";
          break;
        default:
          title = name === 'My 1:1s' ? 'My 1:1s for' : `${name}'s 1:1s for`;
          break;
      }
    }
    return title;
  }, [selectedCohort]);

  return (
    <HeaderMarginContainer>
      <Flex
        alignItems="center"
        bgColor="#F5F7F9"
        w="100%"
        px={(isMobileView() ? 3 : 0) + (!checkNewNavEnabled() ? 4 : 0)}
        py={!checkNewNavEnabled() ? 4 : 0}
        h="48px"
      >
        <TextHeader2 bold mr="8px">
          {updatedTitle ?? queryParams?.name}
        </TextHeader2>

        {(selectedCohort?.name !== 'Settings' || selectedCohort?.type === 'CUSTOM_TYPE') && (
          <Flex>
            <DateRangeDropdown
              onSelect={updateDateRange}
              minDate={new Date(2020, 5, 1, 0, 0, 0, 0)}
              maxDate={addYears(startOfDay(new Date()), 5)}
              initialDateRange={past3Months}
              showPredefinedRanges
            />
          </Flex>
        )}
      </Flex>
    </HeaderMarginContainer>
  );
};

export default OneOnOneHeading;
